import React, { useEffect, useState, useRef } from 'react';
import { ZoomInIcon, ZoomOutIcon, RotateCwIcon, DownloadIcon, PrinterIcon, SearchIcon, MoreHorizontalIcon, MaximizeIcon, MinimizeIcon, MoveHorizontalIcon, MoveVerticalIcon } from 'lucide-react';
import { InvoiceDocument } from '../types/invoice';
interface ImageViewerPanelProps {
  document: InvoiceDocument;
}
type FitMode = 'none' | 'width' | 'height';
export function ImageViewerPanel({
  document
}: ImageViewerPanelProps) {
  const [fitMode, setFitMode] = useState<FitMode>('width');
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [calculatedScale, setCalculatedScale] = useState(1);
  // Calculate scale for fit height mode
  useEffect(() => {
    if (fitMode === 'height' && containerRef.current && contentRef.current) {
      // Use requestAnimationFrame to ensure DOM is updated
      requestAnimationFrame(() => {
        if (!containerRef.current || !contentRef.current) return;
        const containerHeight = containerRef.current.clientHeight - 32; // Account for padding
        const contentHeight = contentRef.current.scrollHeight;
        // Calculate scale to fit height
        const scale = containerHeight / contentHeight;
        console.log('Fit Height Calculation:', {
          containerHeight,
          contentHeight,
          scale
        });
        setCalculatedScale(scale);
      });
    } else {
      setCalculatedScale(1);
    }
  }, [fitMode, document]);
  const handleFitToWidth = () => {
    setFitMode('width');
    setZoom(100);
  };
  const handleFitToHeight = () => {
    setFitMode('height');
    setZoom(100);
  };
  const handleZoomIn = () => {
    setFitMode('none');
    setZoom(prev => Math.min(prev + 25, 300));
  };
  const handleZoomOut = () => {
    setFitMode('none');
    setZoom(prev => Math.max(prev - 25, 25));
  };
  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };
  // Determine container classes based on fit mode
  const getContainerClasses = () => {
    if (fitMode === 'width') {
      return 'w-full';
    } else if (fitMode === 'height') {
      return '';
    }
    return '';
  };
  const getWrapperClasses = () => {
    if (fitMode === 'width') {
      return 'w-full';
    } else if (fitMode === 'height') {
      return 'h-full flex items-start justify-center py-4';
    }
    return 'flex justify-center';
  };
  const getContentStyle = () => {
    const scale = fitMode === 'height' ? calculatedScale : zoom / 100;
    const baseStyle: React.CSSProperties = {
      transform: `rotate(${rotation}deg) scale(${scale})`,
      transformOrigin: 'center center',
      transition: 'transform 0.2s ease-out'
    };
    return baseStyle;
  };
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col h-full overflow-hidden" data-id="element-6124">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50" data-id="element-6125">
        <div className="flex items-center gap-2" data-id="element-6126">
          <button onClick={handleZoomOut} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Zoom Out" data-id="element-6127">
            <ZoomOutIcon className="w-4 h-4 text-gray-600" data-id="element-6128" />
          </button>
          <span className="text-xs text-gray-600 font-medium min-w-[3rem] text-center" data-id="element-6129">
            {zoom}%
          </span>
          <button onClick={handleZoomIn} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Zoom In" data-id="element-6130">
            <ZoomInIcon className="w-4 h-4 text-gray-600" data-id="element-6131" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-6132" />
          <button onClick={handleFitToWidth} className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${fitMode === 'width' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-700'}`} title="Fit to Width" data-id="element-6133">
            <MoveHorizontalIcon className="w-3.5 h-3.5" data-id="element-6134" />
            Fit Width
          </button>
          <button onClick={handleFitToHeight} className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-1.5 ${fitMode === 'height' ? 'bg-blue-100 text-blue-700' : 'hover:bg-gray-200 text-gray-700'}`} title="Fit to Height" data-id="element-6135">
            <MoveVerticalIcon className="w-3.5 h-3.5" data-id="element-6136" />
            Fit Height
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-6137" />
          <button onClick={handleRotate} className="p-1.5 hover:bg-gray-200 rounded transition-colors" title="Rotate" data-id="element-6138">
            <RotateCwIcon className="w-4 h-4 text-gray-600" data-id="element-6139" />
          </button>
          <div className="w-px h-6 bg-gray-300 mx-1" data-id="element-6140" />
          <span className="text-sm text-gray-600" data-id="element-6141">1 of 1</span>
        </div>
        <div className="flex items-center gap-2" data-id="element-6142">
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-6143">
            <SearchIcon className="w-4 h-4 text-gray-600" data-id="element-6144" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-6145">
            <DownloadIcon className="w-4 h-4 text-gray-600" data-id="element-6146" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-6147">
            <PrinterIcon className="w-4 h-4 text-gray-600" data-id="element-6148" />
          </button>
          <button className="p-1.5 hover:bg-gray-200 rounded transition-colors" data-id="element-6149">
            <MoreHorizontalIcon className="w-4 h-4 text-gray-600" data-id="element-6150" />
          </button>
        </div>
      </div>

      {/* Invoice Preview */}
      <div ref={containerRef} className="flex-1 bg-gray-100 overflow-auto" data-id="element-6151">
        <div className={`min-h-full ${getWrapperClasses()}`} data-id="element-6152">
          <div ref={contentRef} className={`bg-white shadow-lg ${getContainerClasses()}`} style={getContentStyle()} data-id="element-6153">
            {/* Invoice Header - FROM VENDOR */}
            <div className="p-8 border-b border-gray-200" data-id="element-6154">
              <div className="flex justify-between items-start" data-id="element-6155">
                {/* Vendor (Supplier) Details - Left Side */}
                <div data-id="element-6156">
                  <div className="font-bold text-2xl mb-2" data-id="element-6157">
                    {document.supplier}
                  </div>
                  <div className="text-xs text-gray-600" data-id="element-6158">
                    <div data-id="element-6159">123 Supplier Street</div>
                    <div data-id="element-6160">Sydney NSW 2000</div>
                    <div data-id="element-6161">Australia</div>
                    <div className="mt-2" data-id="element-6162">Phone: (02) 9999 8888</div>
                    <div data-id="element-6163">ABN: 12 345 678 901</div>
                  </div>
                </div>

                {/* Invoice Title - Right Side */}
                <div className="text-right" data-id="element-6164">
                  <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6165">
                    INVOICE
                  </div>
                  <div className="text-sm font-medium text-gray-700" data-id="element-6166">
                    {document.invoiceNumber}
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Details */}
            <div className="p-8" data-id="element-6167">
              {/* Bill To - Customer (Abbott) */}
              <div className="mb-6 bg-gray-50 p-4 rounded border border-gray-200" data-id="element-6168">
                <div className="text-xs font-bold text-gray-700 mb-2 uppercase" data-id="element-6169">
                  Bill To
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-6170">
                  Abbott Australasia Pty Ltd
                </div>
                <div className="text-xs text-gray-600" data-id="element-6171">
                  <div data-id="element-6172">Level 4, 32-34 Lord Street</div>
                  <div data-id="element-6173">Botany NSW 2019</div>
                  <div data-id="element-6174">Australia</div>
                  <div className="mt-2" data-id="element-6175">Phone: (02) 9384 3000</div>
                  <div data-id="element-6176">ABN: 95 000 180 389</div>
                </div>
              </div>

              {/* Invoice Info */}
              <div className="grid grid-cols-4 gap-4 mb-6 text-xs" data-id="element-6177">
                <div data-id="element-6178">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6179">
                    Invoice Date
                  </div>
                  <div className="font-medium" data-id="element-6180">{document.invoiceDate}</div>
                </div>
                <div data-id="element-6181">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6182">
                    Due Date
                  </div>
                  <div className="font-medium" data-id="element-6183">{document.dueDate}</div>
                </div>
                <div data-id="element-6184">
                  <div className="text-gray-600 mb-1 font-semibold" data-id="element-6185">Terms</div>
                  <div className="font-medium" data-id="element-6186">Net 30 Days</div>
                </div>
                {document.poNumber && <div data-id="element-6187">
                    <div className="text-gray-600 mb-1 font-semibold" data-id="element-6188">
                      PO Number
                    </div>
                    <div className="font-medium text-blue-600" data-id="element-6189">
                      {document.poNumber}
                    </div>
                  </div>}
              </div>

              {/* Line Items */}
              <table className="w-full mb-6 text-xs" data-id="element-6190">
                <thead className="bg-gray-100 border-b-2 border-gray-300" data-id="element-6191">
                  <tr data-id="element-6192">
                    <th className="text-left py-2 px-2 font-semibold" data-id="element-6193">
                      Item Code
                    </th>
                    <th className="text-left py-2 px-2 font-semibold" data-id="element-6194">
                      Description
                    </th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6195">Qty</th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6196">
                      Unit Price
                    </th>
                    <th className="text-right py-2 px-2 font-semibold" data-id="element-6197">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-6198">
                  {document.lineItems.map((item, index) => <tr key={index} className="border-b border-gray-200" data-id="element-6199">
                      <td className="py-3 px-2 font-mono text-xs" data-id="element-6200">
                        {item.itemCode}
                      </td>
                      <td className="py-3 px-2" data-id="element-6201">{item.description}</td>
                      <td className="text-right py-3 px-2" data-id="element-6202">{item.quantity}</td>
                      <td className="text-right py-3 px-2" data-id="element-6203">
                        ${item.unitPrice.toFixed(2)}
                      </td>
                      <td className="text-right py-3 px-2 font-medium" data-id="element-6204">
                        ${item.total.toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end" data-id="element-6205">
                <div className="w-64 text-xs" data-id="element-6206">
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-6207">
                    <span data-id="element-6208">Subtotal:</span>
                    <span className="font-medium" data-id="element-6209">
                      ${document.subtotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200" data-id="element-6210">
                    <span data-id="element-6211">GST (10%):</span>
                    <span className="font-medium" data-id="element-6212">
                      ${document.taxAmt.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-t-2 border-gray-900 font-bold text-base" data-id="element-6213">
                    <span data-id="element-6214">Total {document.currency}:</span>
                    <span data-id="element-6215">${document.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="mt-8 pt-6 border-t border-gray-200" data-id="element-6216">
                <div className="text-xs" data-id="element-6217">
                  <div className="font-semibold mb-2" data-id="element-6218">Payment Details:</div>
                  <div className="text-gray-600" data-id="element-6219">
                    <div data-id="element-6220">Bank: Commonwealth Bank</div>
                    <div data-id="element-6221">BSB: 062 000</div>
                    <div data-id="element-6222">Account: 1234 5678</div>
                    <div className="mt-2" data-id="element-6223">
                      Please include invoice number in payment reference
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 pt-4 border-t border-gray-200 text-center" data-id="element-6224">
                <div className="text-xs text-gray-500" data-id="element-6225">
                  Thank you for your business!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}